import type { RoomCalendarBooking } from '../types';

export const OFFICE_START_MINUTES = 7 * 60 + 30;
export const OFFICE_END_MINUTES = 18 * 60;
export const OFFICE_DURATION_MINUTES = OFFICE_END_MINUTES - OFFICE_START_MINUTES;

export interface MinuteInterval {
  start: number;
  end: number;
}

export interface RoomCalendarBookingSegment extends MinuteInterval {
  booking: RoomCalendarBooking;
  kind: 'BOOKING' | 'RELEASED_HISTORY';
}

const malaysiaDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kuala_Lumpur',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const malaysiaTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function addIsoDays(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

export function malaysiaDateFromIso(iso: string): string {
  const parts = malaysiaDateFormatter.formatToParts(new Date(iso));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function malaysiaMinutesFromIso(iso: string): number {
  const parts = malaysiaTimeFormatter.formatToParts(new Date(iso));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Number(values.hour) * 60 + Number(values.minute);
}

export function timeFromMinutes(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function bookingsForDate(bookings: RoomCalendarBooking[], date: string): RoomCalendarBooking[] {
  return bookings.filter((booking) => malaysiaDateFromIso(booking.starts_at) === date);
}

export function isBlockingBooking(booking: RoomCalendarBooking): boolean {
  return !booking.status || booking.status === 'RESERVED' || booking.status === 'CANCELLED_ROOM_RETAINED';
}

function subtractIntervals(interval: MinuteInterval, blockers: MinuteInterval[]): MinuteInterval[] {
  let remaining = [interval];
  for (const blocker of blockers) {
    remaining = remaining.flatMap((candidate) => {
      if (blocker.end <= candidate.start || blocker.start >= candidate.end) return [candidate];
      const parts: MinuteInterval[] = [];
      if (candidate.start < blocker.start) parts.push({ start: candidate.start, end: blocker.start });
      if (blocker.end < candidate.end) parts.push({ start: blocker.end, end: candidate.end });
      return parts;
    });
  }
  return remaining;
}

export function roomCalendarBookingSegments(
  bookings: RoomCalendarBooking[],
  date: string,
): RoomCalendarBookingSegment[] {
  const datedBookings = bookingsForDate(bookings, date);
  const activeByLineage = new Map<string, MinuteInterval[]>();

  for (const booking of datedBookings) {
    if (!isBlockingBooking(booking)) continue;
    const intervals = activeByLineage.get(booking.reservation_lineage_id) ?? [];
    intervals.push({
      start: malaysiaMinutesFromIso(booking.starts_at),
      end: malaysiaMinutesFromIso(booking.ends_at),
    });
    activeByLineage.set(booking.reservation_lineage_id, intervals);
  }

  return datedBookings.flatMap((booking): RoomCalendarBookingSegment[] => {
    const interval = {
      start: malaysiaMinutesFromIso(booking.starts_at),
      end: malaysiaMinutesFromIso(booking.ends_at),
    };
    if (isBlockingBooking(booking)) {
      return [{ ...interval, booking, kind: 'BOOKING' }];
    }

    return subtractIntervals(
      interval,
      activeByLineage.get(booking.reservation_lineage_id) ?? [],
    ).map((segment) => ({ ...segment, booking, kind: 'RELEASED_HISTORY' }));
  });
}

export function mergedBookedIntervals(bookings: RoomCalendarBooking[], date: string): MinuteInterval[] {
  const intervals = bookingsForDate(bookings, date).filter(isBlockingBooking)
    .map((booking) => ({
      start: Math.max(OFFICE_START_MINUTES, malaysiaMinutesFromIso(booking.starts_at)),
      end: Math.min(OFFICE_END_MINUTES, malaysiaMinutesFromIso(booking.ends_at)),
    }))
    .filter((interval) => interval.start < interval.end)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const merged: MinuteInterval[] = [];
  for (const interval of intervals) {
    const previous = merged[merged.length - 1];
    if (!previous || interval.start > previous.end) merged.push({ ...interval });
    else previous.end = Math.max(previous.end, interval.end);
  }
  return merged;
}

export function availableIntervals(bookings: RoomCalendarBooking[], date: string): MinuteInterval[] {
  const available: MinuteInterval[] = [];
  let cursor = OFFICE_START_MINUTES;
  for (const interval of mergedBookedIntervals(bookings, date)) {
    if (cursor < interval.start) available.push({ start: cursor, end: interval.start });
    cursor = Math.max(cursor, interval.end);
  }
  if (cursor < OFFICE_END_MINUTES) available.push({ start: cursor, end: OFFICE_END_MINUTES });
  return available;
}

export function bookingOverlapsMinutes(
  booking: RoomCalendarBooking,
  date: string,
  startMinutes: number,
  endMinutes: number,
): boolean {
  if (malaysiaDateFromIso(booking.starts_at) !== date || !isBlockingBooking(booking)) return false;
  const bookingStart = malaysiaMinutesFromIso(booking.starts_at);
  const bookingEnd = malaysiaMinutesFromIso(booking.ends_at);
  return bookingStart < endMinutes && bookingEnd > startMinutes;
}

export function timelineStyle(start: number, end: number): { left: string; width: string } {
  const clippedStart = Math.min(OFFICE_END_MINUTES, Math.max(OFFICE_START_MINUTES, start));
  const clippedEnd = Math.min(OFFICE_END_MINUTES, Math.max(OFFICE_START_MINUTES, end));
  return {
    left: `${((clippedStart - OFFICE_START_MINUTES) / OFFICE_DURATION_MINUTES) * 100}%`,
    width: `${(Math.max(0, clippedEnd - clippedStart) / OFFICE_DURATION_MINUTES) * 100}%`,
  };
}
